// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Sign-in and case membership (`docs/gui/openapi.yaml`, tags `auth` and
 * `members`; ruxd server mode, phase S3).
 *
 * The session is an HttpOnly cookie the server sets when the browser trades
 * a Supabase access token for it (`session()`, the bridge — see
 * `app/session.ts`): this client never sees the cookie, and `<img>`, raw
 * `fetch` and the events socket carry it with no extra code. In local mode
 * (`ruxd --local`) `me()` answers with the implicit user and there is no
 * login at all.
 */

import { authAwareFetch } from '../app/unauthorized';
import { ApiRequestError, DEFAULT_BASE_URL, describeFailure } from './client';
import type {
  ApiToken,
  AuthMe,
  AuthUser,
  CaseMember,
  CaseRole,
  CaseSummary,
  CaseGroupShare,
  GlobalRole,
  Group,
  GroupCase,
  GroupMember,
  NewApiToken,
  ShareLink,
  ShareLinkCreated,
  ShareLinkPublic,
  ShareRole,
} from './types';

/** `PATCH /users/{uid}` (admin): only present fields change; `username: null` clears it. */
export interface UserPatch {
  role?: GlobalRole;
  email?: string;
  username?: string | null;
  disabled?: boolean;
}

/**
 * `PATCH /cases/{cid}/share-links/{lid}`: only present fields change;
 * `expires_at: null` = never, `password: null` removes it. Setting or
 * removing the password ends the link's open sessions.
 */
export interface ShareLinkPatch {
  label?: string;
  expires_at?: string | null;
  password?: string | null;
}

/** `POST /cases/{cid}/share-links`. */
export interface ShareLinkCreate {
  label?: string;
  /** 4–200 characters. */
  password?: string;
  /** ISO-8601, in the future, at most 10 years away. */
  expires_at?: string;
}

export type AuthFetch = (
  input: string,
  init?: { method?: string; headers?: Record<string, string>; body?: string; signal?: AbortSignal },
) => Promise<Response>;

export class AuthClient {
  private readonly baseUrl: string;
  private readonly doFetch: AuthFetch;

  constructor(options: { baseUrl?: string; fetch?: AuthFetch } = {}) {
    this.baseUrl = (options.baseUrl ?? DEFAULT_BASE_URL).replace(/\/+$/, '');
    this.doFetch =
      options.fetch ?? ((input, init) => authAwareFetch(input, { ...init, credentials: 'same-origin' }));
  }

  private async send<T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const response = await this.doFetch(url, {
      method,
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
    if (!response.ok) throw new ApiRequestError(response.status, await describeFailure(response), url);
    return (response.status === 204 ? undefined : await response.json()) as T;
  }

  /** Who this browser is signed in as. Rejects with a 401 when nobody. */
  me(signal?: AbortSignal): Promise<AuthMe> {
    return this.send<AuthMe>('GET', '/auth/me', undefined, signal);
  }

  /**
   * The bridge: trade a Supabase access token for ruxd's session cookie.
   * Answers like `me()`. 401: the token is invalid, or the user is unknown
   * to ruxd or banned.
   */
  session(accessToken: string): Promise<AuthMe> {
    return this.send<AuthMe>('POST', '/auth/session', { access_token: accessToken });
  }

  logout(): Promise<void> {
    return this.send<void>('POST', '/auth/logout', {});
  }

  async tokens(signal?: AbortSignal): Promise<ApiToken[]> {
    return (await this.send<{ tokens: ApiToken[] }>('GET', '/auth/tokens', undefined, signal)).tokens;
  }

  /** Create a token; `expiresDays` 0 = never. The token is in the answer once. */
  createToken(name: string, expiresDays: number, cid?: string): Promise<NewApiToken> {
    return this.send<NewApiToken>('POST', '/auth/tokens', {
      name,
      expires_days: expiresDays,
      ...(cid ? { case: cid } : {}),
    });
  }

  revokeToken(id: number): Promise<void> {
    return this.send<void>('DELETE', `/auth/tokens/${id}`);
  }

  /** One case, with the caller's role in it. */
  caseInfo(cid: string, signal?: AbortSignal): Promise<CaseSummary> {
    return this.send<CaseSummary>('GET', `/cases/${encodeURIComponent(cid)}`, undefined, signal);
  }

  async members(cid: string, signal?: AbortSignal): Promise<CaseMember[]> {
    const list = await this.send<{ members: CaseMember[] }>(
      'GET',
      `/cases/${encodeURIComponent(cid)}/members`,
      undefined,
      signal,
    );
    return list.members;
  }

  addMember(cid: string, email: string, role: CaseRole): Promise<CaseMember> {
    return this.send<CaseMember>('POST', `/cases/${encodeURIComponent(cid)}/members`, { email, role });
  }

  setMemberRole(cid: string, userId: string, role: CaseRole): Promise<CaseMember> {
    return this.send<CaseMember>('PATCH', `/cases/${encodeURIComponent(cid)}/members/${encodeURIComponent(userId)}`, { role });
  }

  removeMember(cid: string, userId: string): Promise<void> {
    return this.send<void>('DELETE', `/cases/${encodeURIComponent(cid)}/members/${encodeURIComponent(userId)}`);
  }

  // --- groups (ruxd-wmc.2): flat, no roles inside -------------------------------

  /** Every group (any signed-in user may list them). */
  async groups(signal?: AbortSignal): Promise<Group[]> {
    return (await this.send<{ groups: Group[] }>('GET', '/groups', undefined, signal)).groups;
  }

  /** Admins and group managers. */
  createGroup(name: string): Promise<Group> {
    return this.send<Group>('POST', '/groups', { name });
  }

  renameGroup(gid: string, name: string): Promise<Group> {
    return this.send<Group>('PATCH', `/groups/${encodeURIComponent(gid)}`, { name });
  }

  deleteGroup(gid: string): Promise<void> {
    return this.send<void>('DELETE', `/groups/${encodeURIComponent(gid)}`);
  }

  async groupMembers(gid: string, signal?: AbortSignal): Promise<GroupMember[]> {
    return (
      await this.send<{ members: GroupMember[] }>('GET', `/groups/${encodeURIComponent(gid)}/members`, undefined, signal)
    ).members;
  }

  addGroupMember(gid: string, email: string): Promise<GroupMember> {
    return this.send<GroupMember>('POST', `/groups/${encodeURIComponent(gid)}/members`, { email });
  }

  /** Remove a member; anyone may remove themselves (leave). */
  removeGroupMember(gid: string, uid: string): Promise<void> {
    return this.send<void>('DELETE', `/groups/${encodeURIComponent(gid)}/members/${encodeURIComponent(uid)}`);
  }

  /** Members, group managers and admins. */
  async groupCases(gid: string, signal?: AbortSignal): Promise<GroupCase[]> {
    return (await this.send<{ cases: GroupCase[] }>('GET', `/groups/${encodeURIComponent(gid)}/cases`, undefined, signal))
      .cases;
  }

  // --- a case's group shares (ruxd-wmc.3) --------------------------------------

  async caseGroups(cid: string, signal?: AbortSignal): Promise<CaseGroupShare[]> {
    return (
      await this.send<{ groups: CaseGroupShare[] }>('GET', `/cases/${encodeURIComponent(cid)}/groups`, undefined, signal)
    ).groups;
  }

  /** Case owners and admins, with any group. */
  shareWithGroup(cid: string, groupId: string, role: ShareRole): Promise<CaseGroupShare> {
    return this.send<CaseGroupShare>('POST', `/cases/${encodeURIComponent(cid)}/groups`, { group_id: groupId, role });
  }

  setGroupShareRole(cid: string, groupId: string, role: ShareRole): Promise<CaseGroupShare> {
    return this.send<CaseGroupShare>(
      'PATCH',
      `/cases/${encodeURIComponent(cid)}/groups/${encodeURIComponent(groupId)}`,
      { role },
    );
  }

  unshareGroup(cid: string, groupId: string): Promise<void> {
    return this.send<void>('DELETE', `/cases/${encodeURIComponent(cid)}/groups/${encodeURIComponent(groupId)}`);
  }

  // --- a case's share links (ruxd-wmc.4): case owners and admins only --------

  async shareLinks(cid: string, signal?: AbortSignal): Promise<ShareLink[]> {
    return (
      await this.send<{ share_links: ShareLink[] }>('GET', `/cases/${encodeURIComponent(cid)}/share-links`, undefined, signal)
    ).share_links;
  }

  createShareLink(cid: string, body: ShareLinkCreate): Promise<ShareLinkCreated> {
    return this.send<ShareLinkCreated>('POST', `/cases/${encodeURIComponent(cid)}/share-links`, body);
  }

  updateShareLink(cid: string, id: number, patch: ShareLinkPatch): Promise<ShareLink> {
    return this.send<ShareLink>('PATCH', `/cases/${encodeURIComponent(cid)}/share-links/${id}`, patch);
  }

  revokeShareLink(cid: string, id: number): Promise<void> {
    return this.send<void>('DELETE', `/cases/${encodeURIComponent(cid)}/share-links/${id}`);
  }

  // --- users (admin, ruxd-wmc.6) ----------------------------------------------

  async users(query = '', signal?: AbortSignal): Promise<AuthUser[]> {
    const q = query.trim() ? `?q=${encodeURIComponent(query.trim())}` : '';
    return (await this.send<{ users: AuthUser[] }>('GET', `/users${q}`, undefined, signal)).users;
  }

  patchUser(uid: string, patch: UserPatch): Promise<AuthUser> {
    return this.send<AuthUser>('PATCH', `/users/${encodeURIComponent(uid)}`, patch);
  }

  /** Sends Supabase's recovery mail. */
  sendPasswordReset(uid: string): Promise<void> {
    return this.send<void>('POST', `/users/${encodeURIComponent(uid)}/password-reset`, {});
  }
}

/** A refused share-link call; on a 429, how long the server asks to wait. */
export class ShareRequestError extends ApiRequestError {
  constructor(
    status: number,
    message: string,
    url: string,
    /** Seconds from `Retry-After`; null when absent or not a number of seconds. */
    readonly retryAfterSeconds: number | null,
  ) {
    super(status, message, url);
  }
}

/** `Retry-After` as whole seconds (the delta-seconds form ruxd sends), or null. */
export function retryAfterSeconds(header: string | null): number | null {
  if (header === null || !/^\s*\d+\s*$/.test(header)) return null;
  return Number(header.trim());
}

/**
 * Opening a share link (`/share/{token}`, public). A plain fetch, never the
 * auth-aware one: here a 401 means "wrong password", not "signed out".
 */
export class ShareClient {
  constructor(private readonly baseUrl = DEFAULT_BASE_URL) {}

  private async send<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = `${this.baseUrl.replace(/\/+$/, '')}${path}`;
    const response = await fetch(url, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!response.ok)
      throw new ShareRequestError(
        response.status,
        await describeFailure(response),
        url,
        retryAfterSeconds(response.headers.get('Retry-After')),
      );
    return (await response.json()) as T;
  }

  /** 404 unknown, 410 expired/revoked, 429 slow down. */
  info(token: string): Promise<ShareLinkPublic> {
    return this.send<ShareLinkPublic>('GET', `/share/${encodeURIComponent(token)}`);
  }

  /** Opens the link: sets the share cookie. 401 password missing or wrong. */
  open(token: string, password?: string): Promise<AuthMe> {
    return this.send<AuthMe>('POST', `/share/${encodeURIComponent(token)}/session`, password ? { password } : {});
  }
}

export const shareApi = new ShareClient();

/** The client the shell, the login page and Indstillinger use. */
export const authApi = new AuthClient();
